import { Suspense } from 'react';
import { ChevronDown, Flag, LayoutGrid, Layers, LogOut, Plug, Server, User } from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@/api/auth';
import { useSubscription } from '@/api/billing';
import { useAuth } from '@/app/auth';
import { api } from '@/lib/api';
import { Avatar, Logo, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '@/lib/utils';
import { displayName, initials } from '@/lib/user';
import { useOnboarding } from '@/features/get-started/useOnboarding';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  /** Shown in the mobile bottom tab bar (desktop sidebar shows every entry). */
  mobile?: boolean;
  /** Only render this entry while onboarding is incomplete (first-run). */
  firstRunOnly?: boolean;
  /** Hidden from the mobile bar during first-run, to keep the bar at 5 slots. */
  mobileFirstRunHidden?: boolean;
}

const NAV: NavItem[] = [
  { to: '/dashboard/get-started', label: 'Get started', icon: Flag, mobile: true, firstRunOnly: true },
  { to: '/dashboard/store', label: 'Catalog', icon: LayoutGrid, mobile: true },
  { to: '/dashboard/my-greffons', label: 'My greffons', icon: Layers, mobile: true },
  { to: '/dashboard/greffers', label: 'Greffers', icon: Server, mobile: true },
  // Integrations yields its mobile tab slot to Get-started during first-run
  // (hld-get-started-flow §Open Questions option (a): the mockup's first-run-only
  // swap, keeping the bar at 5 slots). Desktop sidebar always shows it.
  { to: '/dashboard/integrations', label: 'Integrations', icon: Plug, mobile: true, mobileFirstRunHidden: true },
  { to: '/dashboard/account', label: 'Account', icon: User, mobile: true },
];

export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const signOut = useAuth((s) => s.signOut);
  const { data: user } = useUser();
  const { data: subscription } = useSubscription();
  // Onboarding incomplete → show the Get-started entry + its pulse nudge, and on
  // mobile swap it in for Integrations (the mockup's first-run tab set).
  const { firstRun } = useOnboarding();

  // Desktop sidebar: drop the Get-started entry once onboarding is done.
  const sidebarNav = NAV.filter((n) => !n.firstRunOnly || firstRun);
  // Mobile bottom bar: 5 slots. During first-run, Get-started replaces
  // Integrations (preserving order); once done, Integrations returns.
  const mobileNav = NAV.filter(
    (n) => n.mobile && (firstRun ? !n.mobileFirstRunHidden : !n.firstRunOnly),
  );

  const title = NAV.find((n) => pathname.startsWith(n.to))?.label ?? 'Greffon';
  const name = displayName(user) || 'Account';
  const plan = subscription ? `${subscription.plan.display_name}` : 'Self-hosted';

  const onSignOut = async () => {
    try {
      await api.post('/api/auth/logout/', {});
    } catch {
      // Sign out locally even if the backend call fails.
    }
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-page text-fg">
      {/* Desktop sidebar (pinned; only the content pane scrolls) */}
      <aside className="hidden w-56 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface px-3.5 py-4 md:flex">
        <div className="px-2 pb-5">
          <Logo symbol={24} word={18} />
        </div>
        <nav className="flex flex-col gap-0.5">
          {sidebarNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.split('/').pop()}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-soft font-semibold text-brand-deep'
                    : 'text-fg-dim hover:bg-surface-2 hover:text-fg',
                )
              }
            >
              <Icon size={17} strokeWidth={1.8} />
              <span className="flex-1">{label}</span>
              {to === '/dashboard/get-started' && firstRun && (
                <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-brand animate-gpulse" aria-hidden />
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex items-center gap-2.5 border-t border-line px-1 pt-3">
          <Avatar initials={initials(user)} size={30} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-semibold">{name}</div>
            <div className="truncate font-mono text-[10px] text-fg-mute">{plan}</div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-line bg-surface px-4 md:px-6">
          <h1 className="font-sans text-lg font-bold tracking-tight">{title}</h1>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Menu>
              <MenuTrigger asChild>
                <button
                  aria-label="Account menu"
                  className="flex h-9 items-center gap-2 rounded-md border border-line-2 bg-surface-2 px-2 pl-1.5 text-sm font-semibold text-fg focus:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  <Avatar initials={initials(user)} size={26} />
                  <span className="hidden max-w-[120px] truncate sm:block">{name}</span>
                  <ChevronDown size={14} className="text-fg-mute" />
                </button>
              </MenuTrigger>
              <MenuContent>
                <MenuItem onSelect={() => navigate('/dashboard/account')}>
                  <User size={16} /> Account
                </MenuItem>
                <MenuSeparator />
                <MenuItem danger onSelect={onSignOut}>
                  <LogOut size={16} /> Sign out
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Suspense
            fallback={
              <div className="grid h-64 place-items-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-line-2 border-t-brand" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>

        {/* Mobile bottom tab bar — part of the column so only <main> scrolls */}
        <nav className="flex h-16 shrink-0 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] md:hidden">
          {mobileNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`tab-${to.split('/').pop()}`}
              className={({ isActive }) =>
                cn(
                  'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-[10px] font-medium leading-none transition-colors',
                  isActive ? 'text-brand-deep' : 'text-fg-mute',
                )
              }
            >
              <Icon size={19} strokeWidth={1.8} />
              <span className="max-w-full truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
